/**
 * /legal/privacy, /legal/terms, /legal/cookies, /legal/imprint — public (no sign-in), readable on phone and desktop.
 * The text comes from @galaxis/shared (legal-docs.ts); the page only renders it.
 */
import { LEGAL_DOCUMENTS, LEGAL_DOC_KEYS, LEGAL_PATHS, LEGAL_TITLES, type LegalBlock, type LegalDocKey } from '@galaxis/shared';
import { APP_NAME } from '../brand.ts';
import { renderInline } from '../legal/inline.tsx';

export function legalKeyFromPath(pathname: string): LegalDocKey | null {
  const m = pathname.replace(/\/+$/, '').match(/^\/legal\/([a-z-]+)$/);
  const k = m?.[1];
  return k && (LEGAL_DOC_KEYS as readonly string[]).includes(k) ? (k as LegalDocKey) : null;
}

function Block({ b }: { b: LegalBlock }) {
  if ('p' in b) return <p className="legal-p">{renderInline(b.p)}</p>;
  if ('note' in b) return <p className="legal-note">{renderInline(b.note)}</p>;
  if ('ul' in b) return <ul className="legal-ul">{b.ul.map((li, i) => <li key={i}>{renderInline(li)}</li>)}</ul>;
  const { head, rows } = b.table;
  return (
    <div className="legal-table-wrap" tabIndex={0} role="region" aria-label="Táblázat (oldalirányban görgethető)">
      <table className="legal-table">
        <thead><tr>{head.map((h, i) => <th key={i} scope="col">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} data-label={head[ci]}>{renderInline(c)}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export function LegalPage({ docKey }: { docKey: LegalDocKey }) {
  const doc = LEGAL_DOCUMENTS[docKey];
  return (
    <div className="w-full h-full overflow-y-auto bg-space-900" style={{ touchAction: 'pan-y' }} data-testid="legal-page" data-doc={docKey}>
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10 flex flex-col gap-5">
        <header className="flex flex-wrap items-center gap-3">
          <a href="/" className="font-mono text-xl sm:text-2xl font-bold tracking-widest text-accent flex-1 uppercase no-underline inline-flex items-center min-h-[44px]">{APP_NAME}</a>
          <a href="/" className="btn-secondary" data-testid="legal-back">Vissza a játékhoz</a>
        </header>

        <nav aria-label="Jogi dokumentumok" className="flex flex-wrap gap-2">
          {LEGAL_DOC_KEYS.map(k => (
            <a key={k} href={LEGAL_PATHS[k]} aria-current={k === docKey ? 'page' : undefined}
              className={`legal-tab ${k === docKey ? 'legal-tab-active' : ''}`} data-testid={`legal-tab-${k}`}>
              {LEGAL_TITLES[k]}
            </a>
          ))}
        </nav>

        <article className="legal-article">
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-100">{doc.title}</h1>
          <p className="text-xs text-slate-500 mt-1" data-testid="legal-version">Verzió: {doc.version}</p>
          <p className="legal-p mt-4">{renderInline(doc.intro)}</p>
          {doc.sections.map(s => (
            <section key={s.id} id={s.id} className="mt-7">
              <h2 className="text-lg font-semibold text-slate-100 mb-2">{s.title}</h2>
              {s.blocks.map((b, i) => <Block key={i} b={b} />)}
            </section>
          ))}
        </article>

        <p className="legal-note">Ez a tájékoztató a szolgáltatás tényleges működését írja le, és nem jogi tanácsadás.</p>
      </div>
    </div>
  );
}
